import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ThreeGraphCanvas,
  DEFAULT_LAYOUT_CONFIG,
  type GraphLink,
  type GraphNode,
} from './vendor/opentrace-graph';

type HeroNode = {
  id: string;
  type: string;
  label: string;
  path?: string;
  language?: string;
  kind?: string;
};

type HeroLink = {
  source: string;
  target: string;
  type: string;
};

type HeroGraph = {
  nodes: HeroNode[];
  links: HeroLink[];
};

declare global {
  interface Window {
    OPENTRACE_HERO_GRAPH?: HeroGraph;
  }
}

function useElementSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ width: 600, height: 600 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setSize({
        width: Math.max(320, Math.round(rect.width)),
        height: Math.max(320, Math.round(rect.height)),
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

function HeroGraphIsland() {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<React.ElementRef<typeof ThreeGraphCanvas>>(null);
  const { width, height } = useElementSize(hostRef);
  const [graphArmed, setGraphArmed] = useState(false);
  const [hover, setHover] = useState<{
    node: GraphNode;
    x: number;
    y: number;
  } | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const latestHoverRef = useRef<{
    node: GraphNode;
    x: number;
    y: number;
  } | null>(null);

  const rawGraph = window.OPENTRACE_HERO_GRAPH;

  const nodes = useMemo<GraphNode[]>(() => {
    return (rawGraph?.nodes ?? []).map((node) => ({
      id: node.id,
      name: node.label,
      type: node.type,
      properties: {
        path: node.path,
        language: node.language,
        kind: node.kind,
      },
    }));
  }, [rawGraph]);

  const links = useMemo<GraphLink[]>(() => {
    return (rawGraph?.links ?? []).map((link) => ({
      source: link.source,
      target: link.target,
      label: link.type,
    }));
  }, [rawGraph]);

  const layoutConfig = useMemo(
    () => ({
      ...DEFAULT_LAYOUT_CONFIG,
      fa2ScalingRatio: 430,
      fa2Gravity: 0.3,
      noverlapMaxNodes: 0,
      structuralTypes: ['Repository', 'Directory', 'Dependency'],
      flatMode: false,
    }),
    [],
  );

  const compactConfig = useMemo(
    () => ({
      radialStrength: 0.01,
      communityPull: 0,
      centeringStrength: 0,
      radiusScale: 39,
    }),
    [],
  );

  const displayedNodes = graphArmed ? nodes : [];
  const displayedLinks = graphArmed ? links : [];

  const handleNodeHover = (node: GraphNode | null, x: number, y: number) => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (!node) {
      latestHoverRef.current = null;
      setHover(null);
      return;
    }
    latestHoverRef.current = { node, x, y };
    hoverTimerRef.current = window.setTimeout(() => {
      setHover(latestHoverRef.current);
      hoverTimerRef.current = null;
    }, 450);
  };

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0 || graphArmed) return;
    canvas.set3DMode?.(true);
    canvas.setLayoutMode?.('compact');
    canvas.setChargeStrength?.(-430);
    canvas.setLinkDistance?.(480);
    canvas.setCenterStrength?.(0.3);
    canvas.updateCompactConfig?.(compactConfig);
    canvas.setZoomSizeExponent?.(0.5);
    canvas.setEdgeOpacity?.(0.15);
    canvas.set3DSpeed?.(0.0006);
    canvas.set3DTilt?.(0.35);
    canvas.set3DAutoRotate?.(true);
    // Ambient float stays OFF during the build: the worker settles mid-burst,
    // and enabling ambient now would snapshot those mid-build positions as the
    // float centre — then ease every node back to them ~1-2s after the build
    // (a one-time whole-graph rearrange). It's turned on at freeze instead, when
    // positions are final.
    canvas.setAmbientMotion?.(false);
    canvas.setFitDistanceMultiplier?.(2.5);
    canvas.armBuildAnimation?.();

    const showGraph = window.requestAnimationFrame(() => setGraphArmed(true));
    return () => window.cancelAnimationFrame(showGraph);
  }, [compactConfig, graphArmed, nodes.length]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || displayedNodes.length === 0) return;
    canvas.set3DMode?.(true);
    canvas.setLayoutMode?.('compact');
    canvas.setChargeStrength?.(-430);
    canvas.setLinkDistance?.(480);
    canvas.setCenterStrength?.(0.3);
    canvas.updateCompactConfig?.(compactConfig);
    canvas.setZoomSizeExponent?.(0.5);
    canvas.setEdgeOpacity?.(0.15);
    canvas.set3DSpeed?.(0.0006);
    canvas.set3DTilt?.(0.35);
    canvas.set3DAutoRotate?.(true);
    // If the visitor grabs the graph, rotation pauses; ease it back on after
    // ~10s of no interaction.
    canvas.setAutoRotateResumeDelay?.(10000);
    // Ambient float is enabled at freeze (finish), not here — see the arm
    // effect above for why capturing its centre mid-build causes a rearrange.
    canvas.setAmbientMotion?.(false);
    // Frame tighter than the built-in default (radius×3.3) so the graph reads
    // larger. Applied to the auto-fit itself, so the build grows into this
    // framing directly — no post-build zoom/jump.
    canvas.setFitDistanceMultiplier?.(2.5);
    canvas.scheduleAutoFit?.(900);

    // Freeze the layout the instant the build burst finishes. The FA2 layout
    // for a graph this large keeps expanding for ~10s+ after the ~5s burst, so
    // if we let physics run free the graph visibly rearranges/sprawls after the
    // reveal — that read as "broken". We freeze at build-end (the earliest safe
    // point, when rendered positions == layout targets, so freezeLayout()
    // captures the real layout, not mid-flight burst spots).
    //
    // The build can take a moment to *start* (the worker is still initialising
    // on a multi-MB graph), so we wait for it to begin rather than giving up —
    // and freeze anyway if the burst never plays within a safety window, so
    // physics can never run unbounded.
    let frame = 0;
    let done = false;
    let sawBuild = false;
    const startedAt = performance.now();
    const finish = () => {
      if (done) return;
      done = true;
      canvas.freezeLayout?.();
      canvas.stopPhysics?.();
      // Enable the gentle ambient float now — positions are final, so its
      // centre is captured correctly (no post-build drift).
      canvas.setAmbientMotion?.(true);
    };
    const tick = () => {
      if (done) return;
      const animating = canvas.isBuildAnimating?.() ?? false;
      sawBuild ||= animating;
      const elapsed = performance.now() - startedAt;
      // Build burst has played and finished → positions match layout targets.
      if (sawBuild && !animating) {
        finish();
        return;
      }
      // Burst never started within the safety window → freeze anyway.
      if (!sawBuild && elapsed > 12000) {
        finish();
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [compactConfig, displayedNodes.length]);

  if (!rawGraph) return null;

  return (
    <div className="hero__graph-react" ref={hostRef}>
      <ThreeGraphCanvas
        ref={canvasRef}
        nodes={displayedNodes}
        links={displayedLinks}
        width={width}
        height={height}
        layoutConfig={layoutConfig}
        colorMode="type"
        labelsVisible={false}
        edgesEnabled
        communityLabelsVisible={false}
        layoutMode="compact"
        zoomSizeExponent={0.5}
        labelScale={0.72}
        edgeOpacity={0.15}
        chargeStrength={-430}
        linkDistance={480}
        compactConfig={compactConfig}
        mode3d
        rotationSpeed={0.0006}
        cameraTilt={0.35}
        fitDistanceMultiplier={2.5}
        animationSettings={{ selectionPulse: false }}
        onNodeHover={handleNodeHover}
        style={{ background: 'transparent', isolation: 'isolate' }}
      />
      <div
        className={`hero__graph-hover${hover ? ' visible' : ''}`}
        aria-hidden={hover ? 'false' : 'true'}
        style={
          hover
            ? {
                left: Math.min(width - 280, Math.max(8, hover.x + 16)),
                top: Math.min(height - 128, Math.max(8, hover.y + 16)),
              }
            : undefined
        }
      >
        {hover && (
          <>
            <div
              className="hero__graph-hover-type"
              style={{ color: layoutConfig.getNodeColor(hover.node.type) }}
            >
              {hover.node.type}
            </div>
            <div className="hero__graph-hover-name">{hover.node.name}</div>
            {typeof hover.node.properties?.path === 'string' && (
              <div className="hero__graph-hover-path">
                {hover.node.properties.path}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const mount = document.getElementById('heroGraphMount');
if (mount) {
  createRoot(mount).render(<HeroGraphIsland />);
}
